/**
 * Assainissement texte bas niveau pour les exports PDF (jsPDF).
 *
 * WHY (audité 2026-06-16, cf. hub case-wiki) : jsPDF n'embarque que les 14 polices
 * standard encodées en **WinAnsi (cp1252)**. Tout caractère hors de cette table
 * casse le rendu de DEUX façons observées en prod :
 *   1. Il bascule la chaîne ENTIÈRE en encodage UTF-16 2-octets → mojibake.
 *      L'espace fine insécable U+202F, omniprésente en typo FR (« n° 1 »,
 *      « mot : », nombres), a produit « Vérifier⍰dans », « MONTEIRO⍰sur » et
 *      « P i e ce n ° 1 » dans les tables.
 *   2. Les pastilles d'importance ● / ○ (U+25CF/U+25CB) sortaient en « %Ï%Ï ».
 *
 * Parade :
 *   - `toPdfSafe` neutralise les caractères « poison » (espaces Unicode, largeurs
 *     nulles, tirets/moins insécables, primes) AVANT tout `pdf.text`. On NE touche
 *     PAS aux tirets cadratins, guillemets courbes, accents, « », ° : le navigateur
 *     les rend correctement (vérifié sur les exports réels).
 *   - Les pastilles restent dessinées en **vectoriel** côté legalworkspace
 *     (`utils/analysisExport/pdfText.ts` → `drawDots`), jamais en glyphe.
 *
 * WHY ce module vit dans un package et non dans `apps/legalworkspace` : le backend
 * génère désormais lui aussi le PDF d'une décision via le flux
 * `save_jurisprudence` → `saveDecisionAsCaseDocument`
 * et un package ne peut pas importer depuis une app — direction des dépendances.
 */

// Largeurs nulles (ZWSP/ZWNJ/ZWJ/word-joiner/BOM) → supprimées.
const ZERO_WIDTH = /[\u200B-\u200D\u2060\uFEFF]/g;
// Espaces Unicode (NBSP, OGHAM, en→hair, fine insécable U+202F, math, idéo) → espace ASCII.
const UNICODE_SPACES = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

// Caractères cp1252 hors Latin-1 (zone 0x80-0x9F) exprimés en Unicode (€ … œ Œ, tirets et
// guillemets typographiques…). Ils RENDENT correctement (exports navigateur réels) → conservés.
const WINANSI_EXTRA = new Set<number>([
  0x20ac, 0x201a, 0x0192, 0x201e, 0x2026, 0x2020, 0x2021, 0x02c6, 0x2030, 0x0160, 0x2039, 0x0152,
  0x017d, 0x2018, 0x2019, 0x201c, 0x201d, 0x2022, 0x2013, 0x2014, 0x02dc, 0x2122, 0x0161, 0x203a,
  0x0153, 0x017e, 0x0178,
]);

/** Un code-point est-il encodable en WinAnsi (cp1252) par jsPDF ? */
function isWinAnsi(cp: number): boolean {
  return (
    cp === 0x09 || // tab
    cp === 0x0a || // saut de ligne (autotable l'utilise pour les cellules multi-lignes)
    (cp >= 0x20 && cp <= 0x7e) || // ASCII imprimable
    (cp >= 0xa0 && cp <= 0xff) || // Latin-1 supplément (accents FR précomposés, °, «, »…)
    WINANSI_EXTRA.has(cp)
  );
}

/**
 * Normalise une chaîne pour le rendu jsPDF WinAnsi (cp1252).
 *
 * Stratégie « ceinture + bretelles » : on RECOMPOSE (NFC) et on mappe les variantes
 * courantes vers leur équivalent cp1252, PUIS on retire tout caractère restant hors
 * cp1252. WHY : un SEUL caractère non encodable (combinant NFD d'un nom de fichier
 * macOS, signe « № », emoji…) bascule la LIGNE ENTIÈRE en UTF-16 → mojibake
 * (« P i e ce »). Le filet final garantit qu'aucun caractère ne peut plus empoisonner.
 */
export function toPdfSafe(s: string): string {
  const mapped = s
    .normalize("NFC")
    .replace(ZERO_WIDTH, "")
    .replace(UNICODE_SPACES, " ")
    .replace(/[\u0300-\u036F]/g, "") // diacritiques combinants résiduels (non composés par NFC)
    .replace(/\u2116/g, "n°") // signe numéro № → « n° »
    .replace(/[\u2011\u2212]/g, "-") // trait d'union insécable / signe moins → tiret
    .replace(/[\u2032\u2035]/g, "'") // prime → apostrophe
    .replace(/[\u2033\u2036]/g, '"') // double prime → guillemet droit
    .replace(/\u2192/g, "->"); // flèche (rare en juridique)
  // Filet de sécurité : retire tout résidu non cp1252 (sinon poison UTF-16).
  let out = "";
  for (const ch of mapped) {
    const cp = ch.codePointAt(0);
    if (cp !== undefined && isWinAnsi(cp)) out += ch;
  }
  return out;
}
